import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface Option {
  label: string;
  value: number;
}

interface MultiSelectProps {
  options: Option[];
  value: number[];
  onValueChange: (value: number[]) => void;
  placeholder?: string;
}

export function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select items..."
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const selected = options.filter((option) => value.includes(option.value));

  const handleSelect = React.useCallback((selectedValue: number) => {
    onValueChange(
      value.includes(selectedValue)
        ? value.filter((v) => v !== selectedValue)
        : [...value, selectedValue]
    );
  }, [value, onValueChange]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div className="relative flex min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer">
          <div className="flex flex-wrap gap-1">
            {selected.map((item) => (
              <Badge
                key={item.value}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {item.label}
                <button
                  className="rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onValueChange(value.filter((v) => v !== item.value));
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onValueChange(value.filter((v) => v !== item.value));
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {selected.length === 0 && (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No items found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => handleSelect(option.value)}
                >
                  <span className="flex-1">{option.label}</span>
                  {value.includes(option.value) && (
                    <span className="text-xs text-muted-foreground">✓</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
} 
